import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { Theme } from "src/theme";

export type ToastType = "error" | "success" | "info";

type Props = {
  message: string;
  type: ToastType;
  onDismiss: () => void;
  duration?: number;
};

const getBackgroundColor = (type: ToastType): string => {
  switch (type) {
    case "error":
      return "#f44336";
    case "success":
      return "#4caf50";
    case "info":
    default:
      return "#2196f3";
  }
};

const toastStyle =
  (type: ToastType) =>
  ({ gridUnit }: Theme) => ({
    position: "fixed" as const,
    bottom: gridUnit * 5,
    left: "50%",
    transform: "translateX(-50%)",
    padding: `${gridUnit * 3}px ${gridUnit * 5}px`,
    background: getBackgroundColor(type),
    color: "white",
    borderRadius: gridUnit * 1.5,
    boxShadow: `0 ${gridUnit}px ${gridUnit * 3}px rgba(0,0,0,0.2)`,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    gap: gridUnit * 3,
    maxWidth: "90vw",
    fontSize: gridUnit * 4
  });

const dismissButtonStyle = ({ gridUnit }: Theme) => ({
  background: "transparent",
  border: "none",
  color: "white",
  cursor: "pointer",
  padding: gridUnit,
  fontSize: gridUnit * 4,
  lineHeight: 1,
  opacity: 0.8,
  "&:hover": {
    opacity: 1
  }
});

const Toast = ({ message, type, onDismiss, duration = 5000 }: Props): ReactNode => {
  const [isVisible, setIsVisible] = useState<boolean>(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300); // Wait for fade animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  if (!isVisible) return null;

  return (
    <div css={toastStyle(type)} role="alert" aria-live="polite">
      <span>{message}</span>
      <button css={dismissButtonStyle} onClick={onDismiss} aria-label="Dismiss">
        &times;
      </button>
    </div>
  );
};

export default Toast;
